import 'styled-components/macro';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, TextField, Typography, useTheme, LoadingState, ErrorState } from '@harver/shared-ui';

import { makeCandidateUnavailable } from './service';
import { getInterviewDetails } from '../shared/services';
import { INTERVIEW_STATUS, SETTINGS_ERRORS } from '../shared/domains';
import { useContent } from '../../branding/content.context';
import { ContentCard } from '../../components/ContentCard';
import { Confirmation } from './Confirmation';
import { AutocompleteField } from '../../components/SharedStyledElements';
import { SettingDisabled as Disabled } from '../shared/views/SettingDisabled';
import CookieBanner from '../../components/CookieBanner';

type State = {
    sendingMessage: boolean;
    message: string;
    selectedReason: string;
    isMessageSent: any;
};

type InterviewDetails = {
    status: INTERVIEW_STATUS;
    organizationName: string;
};

export function Unavailable() {
    const theme = useTheme();
    const { interviewUnavailable: LANG } = useContent();

    const params = new URLSearchParams(useLocation().search);
    const calendarEventId = params.get('ref') as string;

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    const [state, setState] = useState<State>({
        sendingMessage: false,
        message: '',
        selectedReason: LANG.candidateReasons[0],
        isMessageSent: false,
    });

    const [interviewDetails, setInterviewDetails] = useState<InterviewDetails>({
        organizationName: '',
        status: INTERVIEW_STATUS.pending,
    });
    const [interviewDisabled, setInterviewDisabled] = useState(false);

    const isSubmitDisabled = state.sendingMessage || !state.selectedReason;

    useEffect(() => {
        document.title = 'Candidate Unavailable';

        getInterviewDetails(calendarEventId)
            .then((interviewDetails) => {
                setInterviewDetails(interviewDetails);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [calendarEventId]);

    const makeUnavailable = async (e) => {
        e.preventDefault();

        setState({ ...state, sendingMessage: true });
        try {
            await makeCandidateUnavailable(calendarEventId, state.message.trim(), state.selectedReason);

            setState({ ...state, sendingMessage: false, isMessageSent: true });
        } catch (err) {
            const errorCode = err?.response?.data?.context?.code;
            if (errorCode === SETTINGS_ERRORS.settingDisabled) {
                setInterviewDisabled(true);

                return;
            }

            setError(true);
        }
    };

    if (isLoading) return <LoadingState />;

    if (interviewDisabled) {
        return <Disabled />;
    }

    if (state.isMessageSent || interviewDetails.status === INTERVIEW_STATUS.unavailable) {
        return <Confirmation />;
    }

    if (error) return <ErrorState testId="unavailable.errorScreen" onReloadClick={() => window.location.reload()} />;

    return (
        <ContentCard>
            <form
                onSubmit={makeUnavailable}
                name="cancelForm"
                data-testid="cancel-form"
                css={{
                    margin: `${theme.sizes.xxLarge} 0`,
                }}
            >
                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.medium,
                    }}
                    testId="title"
                    variant="h2"
                >
                    {LANG.title}
                </Typography>

                <Typography
                    css={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.large,
                    }}
                    variant="body2"
                >
                    {LANG.p1}
                </Typography>

                <AutocompleteField
                    css={{ width: '100%', marginBottom: theme.sizes.large }}
                    value={state.selectedReason}
                    options={LANG.candidateReasons}
                    getOptionLabel={(option: string) => option}
                    renderOption={(option: string) => (
                        <span data-testid={`unavailable.option.${option}`}>{option}</span>
                    )}
                    onChange={(event, selectedReason: string): void => setState({ ...state, selectedReason })}
                    renderInput={(params: unknown): JSX.Element => (
                        <TextField
                            {...params}
                            variant="filled"
                            label={LANG.reasonSelectionLabel}
                            testId="select-unavailable-reason"
                        />
                    )}
                />

                <TextField
                    testId="msg-input"
                    variant="filled"
                    css={{ width: '100%', marginBottom: theme.sizes.large }}
                    onChange={(event): void => {
                        setState({ ...state, message: event.target.value });
                    }}
                    multiline={true}
                    maxLength={250}
                    rows={4}
                    placeholder={LANG.p2}
                    inputProps={{
                        maxLength: 10,
                    }}
                />

                <div
                    css={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Button
                        type="submit"
                        color="primary"
                        disabled={isSubmitDisabled}
                        testId="unavailable-interview-submit-button"
                    >
                        {LANG.submitButtonLabel}
                    </Button>
                </div>
            </form>
            <CookieBanner />
        </ContentCard>
    );
}
